// FILE: src/pages/ZReportActualPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

// MUI imports
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from "@mui/material";
import { Autocomplete } from "@mui/material";

/**
 * ZReportActualPage
 * - Shows all "pending" lines for a chosen account, letting the user set an Actual Amount,
 *   and finalize them in bulk. No pagination—everything is fetched at once.
 */
export default function ZReportActualPage() {
  const navigate = useNavigate();

  // All accounts for the dropdown
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");

  // Date user enters for finalizing the lines
  const [actualDate, setActualDate] = useState("");

  // List of pending lines from the server
  const [pendingLines, setPendingLines] = useState([]);

  // For each line: whether user has checked it
  const [checkedMap, setCheckedMap] = useState({});
  // For each line: the user’s typed actualAmount
  const [actualMap, setActualMap] = useState({});
  // For each line: any notes the user typed
  const [notesMap, setNotesMap] = useState({});

  // Fetch accounts on mount
  useEffect(() => {
    loadAccounts();
  }, []);

  // Whenever accountId changes, fetch pending lines
  useEffect(() => {
    if (accountId) {
      fetchPendingLines(accountId);
    } else {
      setPendingLines([]);
    }
  }, [accountId]);

  // 1) Load all accounts (limit large so we get them all)
  async function loadAccounts() {
    try {
      const res = await API.get("/accounts?limit=1000000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Error loading accounts:", err);
    }
  }

  // 2) Fetch all pending lines for the chosen account
  async function fetchPendingLines(accId) {
    try {
      const res = await API.get("/zreports", {
        params: {
          accountId: accId,
          status: "pending",
          limit: 1000000, // fetch them all
        },
      });
      const lines = res.data.data || []; // or res.data if your API returns an array
      setPendingLines(lines);

      // Reset local states for checkboxes, actual amounts, notes
      const newChecked = {};
      const newActual = {};
      const newNotes = {};

      lines.forEach((line) => {
        newChecked[line._id] = false;
        newActual[line._id] = "";
        newNotes[line._id] = "";
      });

      setCheckedMap(newChecked);
      setActualMap(newActual);
      setNotesMap(newNotes);
    } catch (err) {
      console.error("fetchPendingLines Error:", err);
      alert("Failed to load pending lines.");
    }
  }

  // Checkbox toggles
  function handleCheck(lineId, isChecked) {
    setCheckedMap((prev) => ({
      ...prev,
      [lineId]: isChecked,
    }));
    if (!isChecked) {
      // If unchecked, clear out the actual + notes
      setActualMap((prev) => ({ ...prev, [lineId]: "" }));
      setNotesMap((prev) => ({ ...prev, [lineId]: "" }));
    }
  }

  // Actual amount typed
  function handleActualChange(lineId, value) {
    setActualMap((prev) => ({
      ...prev,
      [lineId]: value,
    }));
  }

  // Notes typed
  function handleNotesChange(lineId, value) {
    setNotesMap((prev) => ({
      ...prev,
      [lineId]: value,
    }));
  }

  // Compute total actual for lines user has checked
  function computeTotalActual() {
    return pendingLines.reduce((sum, line) => {
      if (!checkedMap[line._id]) return sum;
      const val = parseFloat(actualMap[line._id]) || 0;
      return sum + val;
    }, 0);
  }

  // For display: how each line’s variance compares to expected
  function computeLinec2bVariance(line) {
    if (!checkedMap[line._id]) return 0;
    const expected = line.expectedAmount || 0;
    const typedActual = parseFloat(actualMap[line._id]) || 0;
    return typedActual - expected;
  }

  // 3) Save => POST /zreports/actual/create
  async function handleSave() {
    if (!accountId || !actualDate) {
      alert("Please select an account and actual date.");
      return;
    }

    // Build lines that are checked
    const linesToSend = pendingLines
      .filter((line) => checkedMap[line._id]) // only the lines user has checked
      .map((line) => ({
        zReportId: line._id,
        actualAmount: parseFloat(actualMap[line._id]) || 0,
        notes: notesMap[line._id],
      }));

    if (linesToSend.length === 0) {
      alert("No lines selected. Please check at least one line to finalize.");
      return;
    }

    const payload = {
      accountId,
      actualDate,
      lines: linesToSend,
    };

    try {
      const res = await API.post("/zreports/actual/create", payload);
      alert(res.data.message || "Z Report Actual saved successfully.");
      navigate("/zreport-list");
    } catch (err) {
      console.error("Error finalizing lines:", err);
      alert(err.response?.data?.message || "Failed to create Z Report Actual.");
    }
  }

  // For sorting the account dropdown
  const accountOptions = useMemo(() => {
    return [...accounts].sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }, [accounts]);

  // The total actual typed by user
  const totalActual = computeTotalActual();

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Z Report Actual Entry
      </Typography>

      {/* Account select */}
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          options={accountOptions}
          getOptionLabel={(option) =>
            `${option.accountName} (${option.accountNo})`
          }
          onChange={(event, newValue) => {
            setAccountId(newValue ? newValue._id : "");
          }}
          value={
            accountOptions.find((acc) => acc._id === accountId) || null
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Account"
              variant="outlined"
              fullWidth
            />
          )}
          ListboxProps={{ style: { maxHeight: 240 } }}
        />
      </Box>

      {/* Actual date input */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Actual Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={actualDate}
          onChange={(e) => setActualDate(e.target.value)}
        />
      </Box>

      <Typography variant="h6" gutterBottom>
        Pending Lines
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Check</TableCell>
              <TableCell>Expected Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Expected Amount</TableCell>
              <TableCell>Actual Amount</TableCell>
              <TableCell>c2bVariance</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No pending lines found.
                </TableCell>
              </TableRow>
            ) : (
              pendingLines.map((line) => {
                const c2bvariance = computeLinec2bVariance(line).toFixed(2);
                return (
                  <TableRow key={line._id}>
                    <TableCell>
                      <Checkbox
                        checked={checkedMap[line._id] || false}
                        onChange={(e) =>
                          handleCheck(line._id, e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {line.expectedDate
                        ? new Date(line.expectedDate).toLocaleDateString()
                        : ""}
                    </TableCell>
                    <TableCell>{line.category?.name || "N/A"}</TableCell>
                    <TableCell>
                      {line.expectedAmount?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        disabled={!checkedMap[line._id]}
                        value={actualMap[line._id]}
                        onChange={(e) =>
                          handleActualChange(line._id, e.target.value)
                        }
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: parseFloat(c2bvariance) < 0 ? "red" : "green",
                        fontWeight: 600,
                      }}
                    >
                      {c2bvariance}
                    </TableCell>
                    <TableCell>
                      <TextField
                        disabled={!checkedMap[line._id]}
                        value={notesMap[line._id]}
                        onChange={(e) =>
                          handleNotesChange(line._id, e.target.value)
                        }
                        multiline
                        rows={1}
                        placeholder="Notes..."
                        inputProps={{ maxLength: 200 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Total Actual: {totalActual.toFixed(2)}
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave}>
          Finalize
        </Button>
      </Box>
    </Container>
  );
}
